import React from "react";
import {
  Alert,
  Box,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import type { Theme } from "@mui/material";
import type { WearComplianceSummary } from "../data/types";

interface WearCompliancePanelProps {
  rows: WearComplianceSummary[];
  /** Confidence below this flags the cohort as low-confidence. */
  lowConfidenceThreshold?: number;
}

const confidenceColor = (score: number, theme: Theme): string => {
  if (score >= 80) return theme.palette.success.main;
  if (score >= 65) return theme.palette.warning.main;
  return theme.palette.error.main;
};

/** Device coverage + data-confidence table, flagging unreliable cohorts. */
export const WearCompliancePanel: React.FC<WearCompliancePanelProps> = ({
  rows,
  lowConfidenceThreshold = 65,
}) => {
  const theme = useTheme();
  const lowConfidence = rows.filter((r) => r.confidenceScore < lowConfidenceThreshold);

  return (
    <Stack spacing={2}>
      {lowConfidence.length > 0 && (
        <Alert severity="warning" variant="outlined">
          Dashboard confidence is reduced for{" "}
          <strong>{lowConfidence.map((r) => r.cohortName).join(", ")}</strong> — wearable
          data is incomplete for these cohorts. Treat their fatigue figures as indicative.
        </Alert>
      )}

      <TableContainer>
        <Table size="small" aria-label="Wear compliance and data confidence by cohort">
          <TableHead>
            <TableRow sx={{ "& th": { fontWeight: 700, fontSize: 12, color: "text.secondary" } }}>
              <TableCell>Cohort</TableCell>
              <TableCell align="right">Active</TableCell>
              <TableCell align="right">Valid days</TableCell>
              <TableCell align="right">Missing days</TableCell>
              <TableCell align="right">Coverage</TableCell>
              <TableCell sx={{ minWidth: 160 }}>Confidence</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.cohortId}>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    {r.cohortName}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  {r.activeUsers}/{r.enrolledUsers}
                </TableCell>
                <TableCell align="right">{r.validDataDays}</TableCell>
                <TableCell align="right">{r.missingDataDays}</TableCell>
                <TableCell align="right">{r.coveragePct}%</TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={r.confidenceScore}
                      aria-label={`Data confidence ${r.confidenceScore} percent`}
                      sx={{
                        flex: 1,
                        height: 8,
                        borderRadius: 999,
                        "& .MuiLinearProgress-bar": {
                          backgroundColor: confidenceColor(r.confidenceScore, theme),
                        },
                      }}
                    />
                    <Typography variant="caption" fontWeight={700} sx={{ minWidth: 32 }}>
                      {r.confidenceScore}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
};
